import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import MediaGalleryEdit from '../components/MediaGalleryEdit';
import useCurrentCompany from '../../modules/react/hooks/useCurrentCompany';
import useFlipper from '../../modules/react/hooks/useFlipper';
import { FLIPPER_INTERNATIONALIZATION } from '../../constants/flippers';
import useQuery from '../../modules/react/hooks/useQuery';
import useMutation from '../../modules/react/hooks/useMutation';
import LoadingIndicator from '../components/LoadingIndicator';

const GET_MEDIUM = gql`
  query GetMedium($id: ID!, $locales: [String]) {
    medium(id: $id) {
      ...MediaGalleryEditMedium
    }
  }

  ${MediaGalleryEdit.fragments.medium}
`;

export const UPDATE_MEDIUM = gql`
  mutation MediumUpdate($id: ID!, $input: MediumInput!) {
    mediumUpdate(id: $id, input: $input) {
      userErrors {
        code
        message
      }
    }
  }
`;

const propTypes = {
  selectedMedium: PropTypes.string.isRequired,
  filter: PropTypes.string.isRequired,
  updateFilter: PropTypes.func.isRequired,
  setBanners: PropTypes.func.isRequired,
  banners: PropTypes.shape({
    success: PropTypes.string,
    errors: PropTypes.arrayOf(PropTypes.string),
    info: PropTypes.string,
  }).isRequired,
  setSelectedMedium: PropTypes.func.isRequired,
};

const defaultProps = {};

const MediaGalleryEditContainer = ({
  selectedMedium,
  updateFilter,
  filter,
  setSelectedMedium,
  banners,
  setBanners,
}) => {
  const { nonDefaultLocales } = useCurrentCompany();
  const i18nEnabled = useFlipper(FLIPPER_INTERNATIONALIZATION);
  const locales = i18nEnabled ? nonDefaultLocales : [];

  const { data, loading } = useQuery(GET_MEDIUM, {
    variables: { id: selectedMedium, locales },
  });

  const [updateMedium, { data: updateData, loading: updateLoading }] = useMutation(UPDATE_MEDIUM, {
    refetchQueries: ['GetAllMedia'],
  });

  if (loading) {
    return <LoadingIndicator />;
  }

  const onUpdateMedium = (input) => {
    const { metadata: inputMetadata, ...rest } = input;

    let metadata = [];
    if (inputMetadata && inputMetadata.length) {
      // filter any values not defined in MediumMetadataInput
      // and any locales without altText or title
      metadata = inputMetadata.map(({ locale, altText, title }) => (
        altText || title ? { locale, altText, title } : null
      )).filter(Boolean);
    }

    updateMedium({ variables: { id: selectedMedium, input: { ...rest, metadata }, locales } });
  };

  return (
    <MediaGalleryEdit
      medium={data.medium}
      i18nEnabled={i18nEnabled}
      locales={locales}
      updateFilter={updateFilter}
      filter={filter}
      selectedMedium={selectedMedium}
      setSelectedMedium={setSelectedMedium}
      banners={banners}
      setBanners={setBanners}
      onUpdateMedium={onUpdateMedium}
      submitting={updateLoading}
      updateData={updateData}
    />
  );
};

MediaGalleryEditContainer.propTypes = propTypes;
MediaGalleryEditContainer.defaultProps = defaultProps;

export default MediaGalleryEditContainer;
